import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AutoExpandingTextarea } from "@/components/ui/auto-expanding-textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { hapticButton, hapticSuccess, hapticError } from "@/utils/haptics";
import { CharacterGuidance } from "@/components/feedback/CharacterGuidance";
import { SubmissionSuccess } from "@/components/feedback/SubmissionSuccess";
import { FileUpload, type UploadedFile } from "@/components/feedback/FileUpload";
import { PasscodeEntry } from "@/components/feedback/PasscodeEntry";
import { logger } from "@/utils/logger";
const feedbackSchemaBase = z.object({
  feedback: z.string()
    .trim()
    .min(10, { message: "Feedback must be at least 10 characters long" })
    .max(1500, { message: "Feedback must be less than 1500 characters" }),
  submitter_name: z.string().optional(),
  submitter_email: z.string().email().optional().or(z.literal('')),
  submitter_role: z.string().optional(),
  submitter_department: z.string().optional(),
});

const FeedbackForm = () => {
  const params = useParams();
  // Support both /f/:slug and /:company/:slug patterns
  const slug = params.slug || params['*']?.split('/').pop();
  const { toast } = useToast();
  
  // Get query params
  const searchParams = new URLSearchParams(window.location.search);
  const campaignSendId = searchParams.get('sendId');
  const isPreviewMode = searchParams.get('preview') === 'true';
  const [page, setPage] = useState<{
    id: string;
    title: string;
    description: string | null;
    user_id: string;
    custom_fields?: unknown;
    form_settings?: unknown;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [trackingCode, setTrackingCode] = useState("");
  const [formStartTime] = useState(Date.now());
  const [attachments, setAttachments] = useState<UploadedFile[]>([]);
  const [showDepartment, setShowDepartment] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);
  
  // Passcode protection state
  const [requiresPasscode, setRequiresPasscode] = useState(false);
  const [passcodeVerified, setPasscodeVerified] = useState(false);
  const [verifiedPasscode, setVerifiedPasscode] = useState<string | null>(null);
  
  // Honeypot for bot detection
  const [honeypot, setHoneypot] = useState("");

  const form = useForm<z.infer<typeof feedbackSchemaBase>>({
    resolver: zodResolver(feedbackSchemaBase),
    defaultValues: {
      feedback: "",
      submitter_name: "",
      submitter_email: "",
      submitter_role: "",
      submitter_department: "",
    },
  });

  // Fetch branding for the page owner using SECURITY DEFINER function
  const { data: branding } = useQuery({
    queryKey: ["branding", page?.user_id],
    queryFn: async () => {
      if (!page?.user_id) {
        return null;
      }
      const { data, error } = await supabase
        .rpc("get_feedback_page_branding", { p_user_id: page.user_id })
        .single();
      if (error) {
        logger.error("Error fetching branding", error);
        return null; // Gracefully fail - don't block the form
      }
      return data;
    },
    enabled: !!page?.user_id,
  });

  // Calculate contrasting text color for button
  const getContrastColor = (hexColor: string | undefined) => {
    if (!hexColor) return undefined;
    
    // Remove # if present
    const hex = hexColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return black for light colors, white for dark colors
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  useEffect(() => {
    loadPage();
  }, [slug]);

  // Update favicon dynamically based on company logo
  useEffect(() => {
    if (branding?.logo_url) {
      const link = document.querySelector("link[rel='icon']") as HTMLLinkElement;
      if (link) {
        link.href = branding.logo_url;
      } else {
        const newLink = document.createElement('link');
        newLink.rel = 'icon';
        newLink.href = branding.logo_url;
        document.head.appendChild(newLink);
      }
    }
  }, [branding?.logo_url]);

  const loadPage = async () => {
    try {
      // If preview mode, check if user is authenticated (page owner)
      if (isPreviewMode) {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          // Authenticated user in preview mode - skip passcode, load page directly
          const { data, error } = await supabase
            .rpc("get_public_feedback_page", { p_slug: slug });

          if (error) throw error;
          
          const pageData = Array.isArray(data) ? data[0] : data;
          
          if (!pageData) {
            throw new Error("Page not found");
          }
          
          setPage(pageData);
          setPasscodeVerified(true);
          setLoading(false);
          return;
        }
        // If not authenticated in preview mode, fall through to normal flow
      }

      // First check if page requires passcode
      const { data: passcodeCheck, error: passcodeError } = await supabase
        .rpc("check_page_passcode_required", { p_slug: slug });
      
      if (passcodeError) throw passcodeError;
      
      const passcodeData = Array.isArray(passcodeCheck) ? passcodeCheck[0] : passcodeCheck;
      
      if (!passcodeData) {
        throw new Error("Page not found");
      }
      
      if (passcodeData.requires_passcode) {
        setRequiresPasscode(true);
        // Store minimal page info for passcode screen
        setPage({
          id: passcodeData.id,
          title: passcodeData.title,
          description: null,
          user_id: '',
          custom_fields: undefined,
          form_settings: undefined,
        });
      } else {
        // No passcode required, load full page data
        const { data, error } = await supabase
          .rpc("get_public_feedback_page", { p_slug: slug });

        if (error) throw error;
        
        const pageData = Array.isArray(data) ? data[0] : data;
        
        if (!pageData) {
          throw new Error("Page not found");
        }
        
        setPage(pageData);
        setPasscodeVerified(true); // No passcode needed
      }
    } catch (error) {
      logger.error("Error loading feedback page", error, { slug });
      toast({
        title: "Page not found",
        description: "This feedback page doesn't exist or is no longer active",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasscodeVerify = async (passcode: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .rpc("verify_page_passcode", { p_slug: slug, p_passcode: passcode });
      
      if (error) throw error;
      
      const result = Array.isArray(data) ? data[0] : data;
      
      if (result?.valid) {
        // Load full page data
        setPage({
          id: result.id,
          title: result.title,
          description: result.description,
          user_id: result.user_id,
          custom_fields: result.custom_fields,
          form_settings: result.form_settings,
        });
        setPasscodeVerified(true);
        setVerifiedPasscode(passcode);
        return true;
      }
      return false;
    } catch (error) {
      logger.error("Error verifying passcode", error);
      return false;
    }
  };

  const handleSubmit = async (values: z.infer<typeof feedbackSchemaBase>) => {
    // Bot detection: reject if honeypot field is filled
    if (honeypot) {
      logger.warn("Bot detected via honeypot", { pageId: page?.id });
      // Silently pretend success to not tip off bots
      setTrackingCode("FB-SUBMITTED");
      setSubmitted(true);
      return;
    }
    
    // Bot detection: reject if submitted too fast (< 3 seconds)
    const timeToComplete = Math.floor((Date.now() - formStartTime) / 1000);
    if (timeToComplete < 3) {
      toast({
        title: "Please take a moment",
        description: "Please review your feedback before submitting",
        variant: "destructive",
      });
      return;
    }
    
    setSubmitting(true);

    try {
      const finalFeedback = values.feedback;
      
      // Use SECURITY DEFINER function for anonymous submissions
      const { data: submissionResult, error } = await supabase
        .rpc("submit_anonymous_feedback", {
          p_page_id: page!.id,
          p_content: finalFeedback,
          p_time_to_complete: timeToComplete,
          p_form_version: "3.2-passcode",
          p_submitter_department: values.submitter_department || null,
          p_interaction_metadata: {
            character_count: finalFeedback.length,
            has_rewrite: rewriting,
            attachment_count: attachments.length,
          },
          p_passcode: verifiedPasscode,
        });

      if (error) {
        logger.error("Submission RPC error", error, {
          pageId: page!.id,
          errorCode: error.code,
        });
        throw new Error("Unable to submit feedback. Please try again.");
      }
      
      // Check for application-level errors from the RPC
      const result = submissionResult as { success: boolean; id?: string; tracking_code?: string; error?: string };
      if (!result?.success) {
        logger.error("Submission failed", new Error(result?.error || "Unknown error"), {
          pageId: page!.id,
        });
        throw new Error(result?.error || "Unable to submit feedback. Please try again.");
      }
      
      const submissionId = result.id as string;
      const tracking = result.tracking_code as string;

      // Link attachments to submission if any
      if (attachments.length > 0 && submissionId) {
        const sessionId = localStorage.getItem('feedback_session_id');
        if (sessionId) {
          try {
            await supabase.rpc('move_temp_attachments_to_submission', {
              p_session_id: sessionId,
              p_submission_id: submissionId,
              p_page_id: page!.id,
            });
          } catch (attachError) {
            logger.error("Error linking attachments", attachError);
            // Don't fail submission if attachment linking fails
          }
        }
      }

      // Track campaign response if this came from a campaign
      if (campaignSendId && submissionId) {
        try {
          await supabase
            .from('campaign_sends')
            .update({ 
              responded_at: new Date().toISOString(),
              submission_id: submissionId 
            })
            .eq('id', campaignSendId);
        } catch (campaignError) {
          logger.warn("Error tracking campaign response", { campaignError, campaignSendId });
          // Don't fail submission if campaign tracking fails
        }
      }

      // Call AI analysis edge function
      try {
        await supabase.functions.invoke("analyze-feedback", {
          body: { 
            submissionId: submissionId,
            pageId: page!.id,
            content: finalFeedback
          },
        });
      } catch (aiError) {
        logger.warn("AI analysis error", { aiError, submissionId });
        // Don't fail the submission if AI analysis fails
      }

      // Clear attachments
      setAttachments([]);
      
      setTrackingCode(tracking);
      setSubmitted(true);
      form.reset();
      
      hapticSuccess();
    } catch (error: unknown) {
      hapticError();
      const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRewrite = async () => {
    const currentText = form.getValues("feedback");
    
    if (!currentText || currentText.trim().length < 10) {
      toast({
        title: "Nothing to rewrite",
        description: "Please write some feedback first (at least 10 characters)",
        variant: "destructive",
      });
      return;
    }

    setRewriting(true);
    try {
      const { data, error } = await supabase.functions.invoke("rewrite-feedback", {
        body: { text: currentText },
      });

      if (error) throw error;

      if (data.success) {
        form.setValue("feedback", data.rewritten_text);
        toast({
          title: "Feedback rewritten",
          description: "Your feedback has been anonymized with AI",
        });
      } else {
        throw new Error(data.error || "Failed to rewrite text");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to rewrite feedback";
      logger.error("Error rewriting feedback", error);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setRewriting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 md:p-6 lg:p-8">
        <Card className="max-w-lg w-full">
          <CardContent className="py-12 text-center">
            <div className="text-4xl mb-4">🔍</div>
            <h2 className="text-xl font-semibold mb-2">Page Not Found</h2>
            <p className="text-muted-foreground">
              This feedback page doesn't exist or is no longer active
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show passcode entry screen if required and not yet verified
  if (requiresPasscode && !passcodeVerified && page) {
    return (
      <PasscodeEntry
        pageTitle={page.title}
        onVerify={handlePasscodeVerify}
        brandColor={branding?.brand_color}
        brandColorContrast={getContrastColor(branding?.brand_color)}
        logoUrl={branding?.logo_url}
        companyName={branding?.company_name}
      />
    );
  }

  if (submitted) {
    return (
      <SubmissionSuccess
        trackingCode={trackingCode}
        onSubmitMore={() => {
          setSubmitted(false);
          setTrackingCode("");
        }}
        brandColor={branding?.brand_color}
        brandColorContrast={getContrastColor(branding?.brand_color)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-start sm:items-center justify-center px-3 py-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-2xl space-y-5 sm:space-y-8">
        {/* Header */}
        <div className="text-center space-y-3 sm:space-y-4">
        {branding?.logo_url && (
          <img 
            src={branding.logo_url} 
            alt={branding.company_name ? `${branding.company_name} logo` : "Company logo"}
            className="h-10 sm:h-12 mx-auto object-contain"
            loading="eager"
            onError={(e) => {
              logger.error("Failed to load logo image", new Error("Logo load failed"), { logoUrl: branding.logo_url });
              e.currentTarget.style.display = 'none';
            }}
          />
        )}
          
          {branding?.company_name && (
            <p className="text-sm font-medium text-muted-foreground">{branding.company_name}</p>
          )}
          
          <div className="space-y-2 sm:space-y-3">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">{page.title}</h1>
            {page.description && (
              <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">{page.description}</p>
            )}
          </div>
        </div>

        {/* Anonymity Banner */}
        <div className="p-3 sm:p-4 bg-primary/10 border border-primary/20 rounded-2xl text-center">
          <p className="text-xs sm:text-sm font-medium text-foreground">
            You can speak freely and safely. Your feedback is truly anonymous — we don't capture anything that could personally identify you in any way.
          </p>
        </div>

        {/* Form Card */}
        <Card className="border-border/50 shadow-sm">
          <CardContent className="pt-4 pb-4 px-4 sm:pt-6 sm:pb-6 sm:px-6 md:px-8">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
                {/* Honeypot field - hidden from users, catches bots */}
                <input
                  type="text"
                  name="website_url"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  className="opacity-0 absolute -z-10 pointer-events-none h-0 w-0 overflow-hidden"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
                
                {/* Feedback Text Area */}
                <FormField
                  control={form.control}
                  name="feedback"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <AutoExpandingTextarea
                          placeholder="Share your thoughts, ideas, or concerns..."
                          {...field}
                          minRows={4}
                          maxRows={20}
                          className="text-base leading-relaxed"
                          autoCapitalize="sentences"
                          autoCorrect="on"
                          spellCheck="true"
                          autoFocus
                        />
                      </FormControl>
                      <div className="space-y-2">
                        <FormMessage />
                        <CharacterGuidance
                          currentLength={field.value?.length || 0}
                          minLength={10}
                          maxLength={1500}
                        />
                      </div>
                    </FormItem>
                  )}
                />

                {/* AI Rewrite, File Upload, Department - compact row, wraps on mobile */}
                <div className="flex flex-wrap items-center gap-2">
                  {/* AI Rewrite */}
                  <div 
                    onClick={() => !rewriting && form.getValues("feedback") && handleRewrite()}
                    className={`flex items-center gap-1.5 px-2 min-h-[40px] bg-primary/5 rounded-xl border border-primary/10 hover:bg-primary/10 transition-colors ${rewriting || !form.getValues("feedback") ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap">Anonymity:</span>
                    <span className="h-6 text-[11px] px-2 justify-center inline-flex items-center rounded border border-input bg-background font-medium hover:bg-accent transition-colors">
                      {rewriting ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        "AI Rewrite"
                      )}
                    </span>
                  </div>

                  {/* File Upload Toggle */}
                  <div 
                    onClick={() => setShowFileUpload(!showFileUpload)}
                    className="flex items-center gap-1.5 px-2 min-h-[40px] bg-primary/5 rounded-xl border border-primary/10 hover:bg-primary/10 transition-colors cursor-pointer"
                  >
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap">Files:</span>
                    <span className="h-6 text-[11px] w-14 justify-center inline-flex items-center rounded border border-input bg-background font-medium hover:bg-accent transition-colors">
                      {showFileUpload ? 'Hide' : 'Add'}
                    </span>
                    {attachments.length > 0 && (
                      <span className="text-[11px] font-medium text-primary ml-1">
                        ({attachments.length})
                      </span>
                    )}
                  </div>

                  {/* Add Department */}
                  <div 
                    onClick={() => setShowDepartment(!showDepartment)}
                    className="flex items-center gap-1.5 px-2 min-h-[40px] bg-primary/5 rounded-xl border border-primary/10 hover:bg-primary/10 transition-colors cursor-pointer"
                  >
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap">Dept:</span>
                    <span className="h-6 text-[11px] w-14 justify-center inline-flex items-center rounded border border-input bg-background font-medium hover:bg-accent transition-colors">
                      {showDepartment ? 'Hide' : 'Add'}
                    </span>
                  </div>
                </div>

                {/* File Upload Component */}
                {showFileUpload && (
                  <FileUpload
                    onFilesChange={setAttachments}
                    existingFiles={attachments}
                    maxFiles={5}
                  />
                )}

                {/* Optional Department Field - Only non-PII context allowed */}
                {showDepartment && (
                  <FormField
                    control={form.control}
                    name="submitter_department"
                    render={({ field }) => (
                      <FormItem>
                        <Label htmlFor="department" className="text-base">
                          Department <span className="text-muted-foreground text-sm">(Optional)</span>
                        </Label>
                        <FormControl>
                          <Input
                            id="department"
                            placeholder="e.g., Engineering, Sales, Customer Support"
                            {...field}
                            className="text-base"
                          />
                        </FormControl>
                        <p className="text-xs text-muted-foreground">
                          This helps us understand feedback patterns by department without identifying you
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Submit Button */}
                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full min-h-[48px] text-base font-medium hover:opacity-90 transition-opacity" 
                  disabled={submitting}
                  style={{ 
                    backgroundColor: branding?.brand_color || undefined,
                    color: getContrastColor(branding?.brand_color),
                  }}
                  onClick={(e) => {
                    if (!submitting) hapticButton();
                  }}
                >
                  {submitting ? "Submitting..." : "Submit Feedback"}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        <div className="text-center space-y-2">
          <p className="text-sm text-muted-foreground">
            Powered by <a href="/" className="hover:text-foreground transition-colors">SendFeedback</a>
          </p>
          <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
            <a href="/how-anonymity-works" target="_blank" className="hover:text-foreground transition-colors underline">
              How anonymity works
            </a>
            <span>•</span>
            <a href="/privacy-policy" target="_blank" className="hover:text-foreground transition-colors underline">
              Privacy Policy
            </a>
            <span>•</span>
            <a href="/terms-of-service" target="_blank" className="hover:text-foreground transition-colors underline">
              Terms
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackForm;
